import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Color } from 'three';

import { isValidNumber } from '../../../../../../../utils/math/number';
import { isDefined } from '../../../../../../../utils/variables';
import { getDefaultEnumValueColor } from '../../../../../../../viewer/elements/design/colors';
import {
  getUnitType,
  SkyMapExtrasUnitType,
  ValueId,
} from '../../../../../../../viewer/elements/design/skymap-extras';
import { DesignTool } from '../../../../../../../viewer/tools/design-tool';
import { importFunctions } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/commands/features/import-geometry';
import { useNonce } from '../../../../../../hooks/use-nonce';
import { Button } from '../../../../../button/button';
import { Dialog } from '../../../../../dialog/dialog';
import { FileSelectionButton } from '../../../../../file-selection-button/file-selection-button';
import { InfoBox } from '../../../../../info-box/info-box';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import { RadioButtons } from '../../../../../radio-buttons/radio-buttons';
import { Stack } from '../../../../../stack/stack';
import { TextBox } from '../../../../../text-box/text-box';
import { EnumValues } from '../sections/attribute/enum-values';
import { UnitValues } from '../sections/attribute/unit-values';

type Props = {
  tool: DesignTool;
  onCreateNewAttribute: (
    tool: DesignTool,
    name: string,
    type: (typeof attributeType)[number],
    values: Map<ValueId, string>,
    colors: Map<ValueId, Color>,
    newUnit: SkyMapExtrasUnitType | string,
    csvResult?: { name: string; headers: string[]; rows: string[][] },
  ) => void;
  onClose: () => void;
};

const attributeType = ['enum', 'unit', 'text', 'group'] as const;

export const NewAttributeDialog = (props: Props) => {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = React.useState<(typeof attributeType)[number]>('enum');
  const [newAttributeName, setNewAttributeName] = React.useState<string>('');
  const [values] = React.useState<Map<ValueId, string>>(new Map());
  const [colors] = React.useState<Map<ValueId, Color>>(new Map());
  const [unit, setUnit] = React.useState<SkyMapExtrasUnitType | string>(getUnitType('none'));
  const [csvResult, setCsvResult] = useState<string[][]>();
  const [csvHeaders, setCsvHeaders] = useState<
    { id: string; name: string; exampleValue: string }[]
  >([]);
  const [hasCsvError, setHasCsvError] = useState(false);
  const [nonce, setNonce] = useNonce();

  const isValid =
    !isDefined(
      props.tool.asset.designNode.getAttributes().find((x) => x.name === newAttributeName),
    ) && newAttributeName.trim().length > 0;

  return (
    <Dialog closeIcon={false} closeOnDimmerClick={false} width={350} onClose={props.onClose}>
      {{
        header: isDefined(csvResult)
          ? t('section.attributes.attributeList.content.newAttributeDialog.codeList.title', {
              ns: 'skyviewDesign',
            })
          : t('section.attributes.attributeList.content.newAttributeDialog.attribute.title', {
              ns: 'skyviewDesign',
            }),
        content: (
          <Form>
            {isDefined(csvResult) ? (
              <Stack direction="column" spacing={1}>
                <InfoBox color="yellow">
                  <Trans
                    components={{ br: <br /> }}
                    i18nKey="section.attributes.attributeList.content.newAttributeDialog.codeList.info"
                    ns="skyviewDesign"
                  />
                </InfoBox>

                <LabelledContainer
                  text={t(
                    'section.attributes.attributeList.content.newAttributeDialog.codeList.nameTitle',
                    {
                      ns: 'skyviewDesign',
                    },
                  )}
                >
                  {(formElementId) => (
                    <TextBox
                      autoFocus={true}
                      id={formElementId()}
                      padding={0.2}
                      value={newAttributeName}
                      onChange={(e) => setNewAttributeName(e.target.value)}
                    />
                  )}
                </LabelledContainer>

                <LabelledContainer
                  text={t(
                    'section.attributes.attributeList.content.newAttributeDialog.codeList.columnsTitle',
                    {
                      ns: 'skyviewDesign',
                    },
                  ).toUpperCase()}
                >
                  <Stack direction="column" spacing={1}>
                    {csvHeaders.map((x, idx) => (
                      <LabelledContainer
                        key={idx}
                        text={t(
                          'section.attributes.attributeList.content.newAttributeDialog.codeList.columnExample',
                          {
                            ns: 'skyviewDesign',
                            value: x.exampleValue,
                          },
                        )}
                      >
                        <TextBox
                          defaultValue={x.name}
                          onChange={(e) => {
                            const newArray = csvHeaders.slice();
                            newArray[idx].name = e.target.value;
                            setCsvHeaders(newArray);
                          }}
                        />
                      </LabelledContainer>
                    ))}
                  </Stack>
                </LabelledContainer>
              </Stack>
            ) : (
              <Stack direction="column" spacing={1}>
                {hasCsvError && (
                  <InfoBox color="red">
                    <Trans
                      components={{ br: <br /> }}
                      i18nKey="section.attributes.attributeList.content.newAttributeDialog.codeList.parseCsvError"
                      ns="skyviewDesign"
                    />
                  </InfoBox>
                )}

                <LabelledContainer
                  text={t(
                    'section.attributes.attributeList.content.newAttributeDialog.attribute.nameTitle',
                    {
                      ns: 'skyviewDesign',
                    },
                  )}
                >
                  {(formElementId) => (
                    <TextBox
                      autoFocus={true}
                      id={formElementId()}
                      padding={0.2}
                      value={newAttributeName}
                      onChange={(e) => setNewAttributeName(e.target.value)}
                    />
                  )}
                </LabelledContainer>

                <LabelledContainer
                  text={t(
                    'section.attributes.attributeList.content.newAttributeDialog.attribute.typeTitle',
                    {
                      ns: 'skyviewDesign',
                    },
                  )}
                >
                  <RadioButtons
                    items={[
                      {
                        value: attributeType['0'],
                        name: t(
                          'section.attributes.attributeList.content.newAttributeDialog.attribute.typeItems.item1',
                          { ns: 'skyviewDesign' },
                        ),
                      },
                      {
                        value: attributeType['1'],
                        name: t(
                          'section.attributes.attributeList.content.newAttributeDialog.attribute.typeItems.item2',
                          { ns: 'skyviewDesign' },
                        ),
                      },
                      {
                        value: attributeType['2'],
                        name: t(
                          'section.attributes.attributeList.content.newAttributeDialog.attribute.typeItems.item3',
                          { ns: 'skyviewDesign' },
                        ),
                      },
                    ]}
                    onChange={(x) => setSelectedType(x)}
                  />
                </LabelledContainer>

                {selectedType === 'enum' && (
                  <EnumValues
                    colorMap={colors}
                    nonce={nonce}
                    valueMap={values}
                    onColorUpdated={(id, color) => {
                      colors.set(id, color);
                      setNonce();
                    }}
                    onRename={(id, value) => {
                      values.set(id, value);
                      setNonce();
                    }}
                    onValueAdded={(id, value) => {
                      values.set(id, value);
                      colors.set(id, getDefaultEnumValueColor(id));
                      setNonce();
                    }}
                    onValueRemoved={(id) => {
                      values.delete(id);
                      colors.delete(id);
                      setNonce();
                    }}
                  />
                )}

                {selectedType === 'unit' && (
                  <UnitValues
                    setUnit={(unit) => {
                      setUnit(unit);
                    }}
                    unit={unit}
                  />
                )}
              </Stack>
            )}
          </Form>
        ),
        footer: {
          left: !isDefined(csvResult) && (
            <FileSelectionButton
              accept=".csv,.txt"
              color="primary"
              leftIcon={{ icon: ['fad', 'upload'] }}
              text={t('import', { ns: 'common' })}
              variant="contained"
              onFilesSelected={(files) => {
                function handleError(e: unknown) {
                  console.error(e);
                  setHasCsvError(true);
                  setCsvResult(undefined);
                }

                try {
                  const reader = new FileReader();
                  reader.onload = (e: ProgressEvent<FileReader>) => {
                    try {
                      const arrayBuffer = e.target?.result as ArrayBuffer;
                      // Detect if it's valid UTF-8, otherwise fall back to windows-1252
                      let encoding: string;
                      try {
                        new TextDecoder('utf-8', { fatal: true }).decode(arrayBuffer);
                        encoding = 'UTF-8';
                      } catch {
                        encoding = 'windows-1252';
                      }
                      const decoder = new TextDecoder(encoding);
                      const text = decoder.decode(arrayBuffer);
                      const result = importFunctions.parseCsv(text);

                      if (result.length === 0) {
                        throw new Error('Empty CSV-file');
                      }

                      const firstRow = result[0];
                      const columnCount = firstRow.length;

                      setSelectedType('group');
                      setCsvResult(result);

                      /**
                       * Arbitrary check if the CSV-file conforms to MOBA:s code list format.
                       * (i.e first column is a numeric value and second column is a single letter value like for example "P", "L" or "S")
                       *
                       * Note: this will only affect the default names for the columns.
                       */
                      const isMobaCsvFile =
                        columnCount > 1 &&
                        isValidNumber(Number(firstRow[0])) &&
                        firstRow[1].length === 1;

                      const defaultColumns = isMobaCsvFile
                        ? [
                            t(
                              'section.attributes.attributeList.content.newAttributeDialog.attribute.defaultMobaColumns.column1',
                              { ns: 'skyviewDesign' },
                            ),
                            t(
                              'section.attributes.attributeList.content.newAttributeDialog.attribute.defaultMobaColumns.column2',
                              { ns: 'skyviewDesign' },
                            ),
                            t(
                              'section.attributes.attributeList.content.newAttributeDialog.attribute.defaultMobaColumns.column3',
                              { ns: 'skyviewDesign' },
                            ),
                            t(
                              'section.attributes.attributeList.content.newAttributeDialog.attribute.defaultMobaColumns.column4',
                              { ns: 'skyviewDesign' },
                            ),
                            t(
                              'section.attributes.attributeList.content.newAttributeDialog.attribute.defaultMobaColumns.column5',
                              { ns: 'skyviewDesign' },
                            ),
                            t(
                              'section.attributes.attributeList.content.newAttributeDialog.attribute.defaultMobaColumns.column6',
                              { ns: 'skyviewDesign' },
                            ),
                          ]
                        : [
                            t(
                              'section.attributes.attributeList.content.newAttributeDialog.attribute.defaultColumns.column1',
                              { ns: 'skyviewDesign' },
                            ),
                            t(
                              'section.attributes.attributeList.content.newAttributeDialog.attribute.defaultColumns.column2',
                              { ns: 'skyviewDesign' },
                            ),
                          ];

                      setCsvHeaders(
                        firstRow.map((x, idx) => {
                          return {
                            id: idx.toString(),
                            name:
                              defaultColumns[idx] ??
                              t(
                                'section.attributes.attributeList.content.newAttributeDialog.codeList.attributeName',
                                { ns: 'skyviewDesign', index: idx + 1 },
                              ),
                            exampleValue: x,
                          };
                        }),
                      );

                      setNewAttributeName(files[0].name);
                    } catch (e) {
                      handleError(e);
                    }
                  };

                  // TODO: Handle error
                  reader.onerror = handleError;
                  reader.readAsArrayBuffer(files[0]);
                } catch (err) {
                  handleError(err);
                }
              }}
            />
          ),

          right: (
            <>
              <Button
                color="primary"
                disabled={!isValid || (selectedType === 'enum' && values.size === 0)}
                variant="contained"
                onClick={() => {
                  props.onCreateNewAttribute(
                    props.tool,
                    newAttributeName,
                    selectedType,
                    values,
                    colors,
                    unit,
                    csvResult
                      ? {
                          name: newAttributeName,
                          headers: csvHeaders.map((x) => x.name),
                          rows: csvResult,
                        }
                      : undefined,
                  );
                  props.onClose();
                }}
              >
                {t('create', { ns: 'common' })}
              </Button>

              <Button
                variant="text"
                onClick={() => {
                  props.onClose();
                }}
              >
                {t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const Form = styled.div`
  min-height: 320px;
  padding: 0.4em;
`;
